import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Tax, TypeTax } from '@tacliatech/types';
import { checkCountryCodeInLocalStorage } from '@web-frontend/shared/utils/country-validator';

interface ITaxResponse {
  id: string;
  name: string;
  value: number;
  type: string;
}

interface ITaxGroupItem extends Omit<ITaxResponse, 'value'> {
  rate: number;
  description: string;
  baseRate?: number;
  surcharge?: number;
}

interface ITaxGroup {
  id: string;
  description: string;
  taxes: ITaxGroupItem[];
}

@Injectable({
  providedIn: 'root',
})
export class TaxService {
  private readonly API_URL = 'https://cdn.taclia.com/json/other-taxes.json';
  private readonly API_ES_URL = 'https://cdn.taclia.com/json/es-taxes.json';
  private taxes: Tax[] = [];
  private taxesSubject = new BehaviorSubject<Tax[]>([]);
  private isLoaded = false;

  constructor(private http: HttpClient) {}

  /**
   * Load taxes from API and populate internal state
   */
  loadTaxes(): Observable<Tax[]> {
    if (this.isLoaded) {
      return this.taxesSubject.asObservable();
    }

    return this.getTaxes().pipe(
      map((taxes: Tax[]) => this.sort(taxes)),
      tap((sortedTaxes: Tax[]) => {
        this.taxes = sortedTaxes;
        this.taxesSubject.next(sortedTaxes);
        this.isLoaded = true;
      })
    );
  }

  /**
   * Get all taxes (synchronous - returns cached data)
   * Call loadTaxes() first to ensure data is available
   */
  findAll(): Tax[] {
    return this.taxes;
  }

  /**
   * Get all taxes as Observable (reactive)
   */
  findAll$(): Observable<Tax[]> {
    if (!this.isLoaded) {
      return this.loadTaxes();
    }
    return this.taxesSubject.asObservable();
  }

  /**
   * Check if taxes are already loaded
   */
  isDataLoaded(): boolean {
    return this.isLoaded;
  }

  private loadTaxesForSpain(): boolean {
    return checkCountryCodeInLocalStorage('ES');
  }

  private getTaxes(): Observable<Tax[]> {
    if (this.loadTaxesForSpain()) {
      return this.getTaxesForSpain();
    }

    return this.getOtherTaxes();
  }

  private getTaxesForSpain(): Observable<Tax[]> {
    return this.http.get<{ taxGroups: ITaxGroup[] }>(this.API_ES_URL).pipe(
      map(({ taxGroups }: { taxGroups: ITaxGroup[] }) => {
        return taxGroups.reduce((allTaxes: Tax[], group: ITaxGroup) => {
          const groupTaxes = group.taxes.map((taxItem: ITaxGroupItem) => ({
            _id: taxItem.id,
            translate: group.id,
            name: taxItem.name,
            value: taxItem.rate,
            type: group.id as TypeTax,
          }));
          return allTaxes.concat(groupTaxes);
        }, []);
      })
    );
  }

  private getOtherTaxes(): Observable<Tax[]> {
    return this.http.get<{ taxes: ITaxResponse[] }>(this.API_URL).pipe(
      map(({ taxes }: { taxes: ITaxResponse[] }) =>
        taxes.map((tax) => ({
          _id: tax.id,
          translate: this.getDefaultTranslate(tax.type as TypeTax),
          name: tax.name,
          value: tax.value,
          type: tax.type as TypeTax,
        }))
      )
    );
  }

  private getDefaultTranslate(type: TypeTax): string {
    switch (type) {
      case TypeTax.IVA:
        return 'taxes.tax';
      case TypeTax.RET:
        return 'taxes.retention';
      case TypeTax.REC:
        return 'taxes.rec';
      default:
        return 'taxes.tax';
    }
  }

  sort(taxes: Tax[]): Tax[] {
    // Group by type
    const groups = taxes.reduce((groups, item) => {
      const group = groups[item.translate] || [];
      group.push(item);
      groups[item.translate] = group;
      return groups;
    }, {});

    // Sort each group
    for (const type in groups) {
      groups[type].sort((a: Tax, b: Tax) => a.value - b.value);
    }

    return Object.values(groups).flat();
  }
}
